/**
 * #325: redeeming an employer invite for a user who is ALREADY signed in.
 *
 * An invite link (SMS or email) carries ?inviteEmployer=&inviteEmployee=. When
 * the employee is signed out, the login screens redeem it right after the OTP /
 * email link. When they are signed in, the router used to send BOTH links to
 * "/" and drop the query string with them — so an employee whose file was
 * deleted and recreated stayed locked on the deleted calculation, and no
 * re-invite could ever reach them. That is what this screen fixes.
 *
 * Redeeming as the current user is safe: redeemEmployeeInvite matches the
 * invite's phone/email against the signed-in identity and refuses otherwise.
 * The one case we do NOT auto-redeem is a manager account — redeeming
 * OVERWRITES the caller's claims with `role: employee`, so an employer admin
 * who taps an invite link to see what the employee sees would demote
 * themselves. They get the sign-out route instead.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { auth, httpsCallable } from '../../services/firebase/app'
import useUser from '../../services/useUser'
import { revokeThisDevice } from '../../services/platform/nativeDeviceRegistry'
import { Root, CardActions } from './Login.styled'

const MANAGER_ROLES = ['admin', 'employerAdmin', 'payrollAccountant']

export function isManagerClaims(claims) {
  return Boolean(
    MANAGER_ROLES.includes(claims?.role) || claims?.employerAdmin
  )
}

export function readInviteParams(search) {
  const params = new URLSearchParams(search)
  const employerId = params.get('inviteEmployer')
  const calculationId = params.get('inviteEmployee')
  return employerId && calculationId ? { employerId, calculationId } : null
}

export default function InviteRedeem() {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useUser()
  const claims = user?.customClaims
  const claimsLoading = user?.claimsLoading
  const invite = readInviteParams(location.search)
  const [error, setError] = useState('')
  const attempted = useRef(false)

  const signOutAndRetry = useCallback(async () => {
    // #1691, spec.md §5.2: a real sign-out revokes the NATIVE_DEVICE
    // server-side too — no-op on web. Swallowed on failure, same reasoning
    // as SideMenu.js's handleSignOut: a device-revoke failure must never
    // block the user from actually signing out.
    try {
      await revokeThisDevice('user_logout')
    } catch {
      // Intentionally swallowed — see comment above.
    }
    // Staying on this URL is deliberate: signed out, the very same path renders
    // the phone-login / email-link screen, which already knows how to redeem.
    await auth.signOut()
  }, [])

  useEffect(() => {
    if (!invite || attempted.current || claimsLoading) return
    if (isManagerClaims(claims)) return
    attempted.current = true

    httpsCallable('redeemEmployeeInvite')({
      employerId: Number(invite.employerId) || invite.employerId,
      calculationId: Number(invite.calculationId) || invite.calculationId,
    })
      .then(() => auth.currentUser?.getIdToken(true))
      .then(() => {
        navigate(
          `/employers/${invite.employerId}/calculations/${invite.calculationId}/timesheets`,
          { replace: true }
        )
      })
      .catch((err) => {
        console.error('redeemEmployeeInvite failed', err)
        setError(err?.message || 'קישור ההזמנה לא הושלם')
      })
  }, [invite, claims, claimsLoading, navigate])

  if (!invite) {
    return <Navigate to="/" replace />
  }

  const isManager = !claimsLoading && isManagerClaims(claims)

  if (!error && !isManager) {
    return (
      <Root>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              מחבר אותך לתיק העובד...
            </Typography>
            <CircularProgress />
          </CardContent>
        </Card>
      </Root>
    )
  }

  return (
    <Root>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            קישור הזמנה לעובד
          </Typography>
          <Alert severity={isManager ? 'info' : 'error'}>
            {isManager
              ? 'הקישור הזה מיועד לעובד, ואתה מחובר כמשתמש ניהולי. ' +
                'כדי לפדות אותו יש להתנתק ולהתחבר עם הזהות שאליה נשלחה ההזמנה.'
              : error}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2 }}>
            אם ההזמנה נשלחה לטלפון או למייל אחרים מאלה שמחוברים כרגע במכשיר,
            יש להתנתק ואז לפתוח שוב את הקישור מההודעה.
          </Typography>
        </CardContent>
        <CardActions>
          <Button variant="contained" color="primary" onClick={signOutAndRetry}>
            התנתקות ופתיחת ההזמנה מחדש
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate('/', { replace: true })}
          >
            חזרה למסך הראשי
          </Button>
        </CardActions>
      </Card>
    </Root>
  )
}
